export const WEB_DICTATION_DOUBLE_TAP_MS = 350;
export const WEB_DICTATION_MAX_DURATION_MS = 5 * 60 * 1_000;
const WEB_DICTATION_HISTORY_LIMIT = 10;

export const getWebDictationRemainingMs = (
  startedAt: number,
  now: number,
): number =>
  Math.max(0, WEB_DICTATION_MAX_DURATION_MS - Math.max(0, now - startedAt));

export const formatWebDictationRemaining = (remainingMs: number): string => {
  const totalSeconds = Math.ceil(Math.max(0, remainingMs) / 1_000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

export const formatWebDictationElapsed = (elapsedMs: number): string => {
  const totalSeconds = Math.floor(Math.max(0, elapsedMs) / 1_000);
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    : `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const WEB_DICTATION_HISTORY_KEY = "webDictationHistory";
export const WEB_DICTATION_MIC_PROMPTED_KEY = "webDictationMicPrompted";

export type WebDictationStatus =
  | "idle"
  | "arming"
  | "listening"
  | "transcribing"
  | "review"
  | "error"
  | "paywall";

export type WebDictationStateEvent =
  | "start"
  | "microphoneReady"
  | "finish"
  | "cancel"
  | "success"
  | "failure"
  | "paywallRequired"
  | "retry"
  | "dismiss";

export const transitionWebDictationState = (
  state: WebDictationStatus,
  event: WebDictationStateEvent,
): WebDictationStatus => {
  switch (state) {
    case "idle":
    case "review":
      if (event === "start") return "arming";
      if (event === "cancel" || event === "dismiss") return "idle";
      return state;
    case "arming":
      if (event === "microphoneReady") return "listening";
      if (event === "failure") return "error";
      if (event === "cancel" || event === "dismiss") return "idle";
      return state;
    case "listening":
      if (event === "finish") return "transcribing";
      if (event === "failure") return "error";
      if (event === "cancel" || event === "dismiss") return "idle";
      return state;
    case "transcribing":
      if (event === "success") return "review";
      if (event === "failure") return "error";
      if (event === "paywallRequired") return "paywall";
      if (event === "cancel" || event === "dismiss") return "idle";
      return state;
    case "error":
      if (event === "retry") return "transcribing";
      if (event === "start") return "arming";
      if (event === "cancel" || event === "dismiss") return "idle";
      return state;
    case "paywall":
      return event === "dismiss" || event === "cancel" ? "idle" : state;
  }
};

type DesktopDictationShortcutMode = WebDictationStatus;
type DesktopDictationTapEffect = "start" | "stop" | "retry";

type DesktopDictationTapState = {
  pendingTapAt?: number;
};

export const createDesktopDictationTapState =
  (): DesktopDictationTapState => ({});

export const reduceDesktopDictationTap = (
  state: DesktopDictationTapState,
  tapAt: number,
  mode: DesktopDictationShortcutMode,
): { state: DesktopDictationTapState; effect?: DesktopDictationTapEffect } => {
  if (mode === "listening") return { state: {}, effect: "stop" };
  if (mode !== "idle" && mode !== "review" && mode !== "error") {
    return { state: {} };
  }
  if (
    state.pendingTapAt !== undefined &&
    tapAt - state.pendingTapAt <= WEB_DICTATION_DOUBLE_TAP_MS
  ) {
    return {
      state: {},
      effect: mode === "error" ? "retry" : "start",
    };
  }
  return { state: { pendingTapAt: tapAt } };
};

type DictationVocabularyRule = {
  from: string;
  to: string;
  kind: "text";
};

const _isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const _parseVocabularyRules = (
  rulesJson: string,
): DictationVocabularyRule[] => {
  if (!rulesJson) return [];
  try {
    const value: unknown = JSON.parse(rulesJson);
    if (!Array.isArray(value)) return [];
    return value.flatMap((rule): DictationVocabularyRule[] => {
      if (
        !_isRecord(rule) ||
        typeof rule.from !== "string" ||
        !rule.from ||
        typeof rule.to !== "string" ||
        rule.kind !== "text"
      ) {
        return [];
      }
      return [
        {
          from: rule.from,
          to: rule.to,
          kind: rule.kind,
        },
      ];
    });
  } catch {
    return [];
  }
};

const _escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const _codePointBefore = (value: string, index: number): string | undefined => {
  const codePoints = Array.from(value.slice(0, index));
  return codePoints[codePoints.length - 1];
};

const _codePointAfter = (value: string, index: number): string | undefined =>
  Array.from(value.slice(index))[0];

const _isWordCodePoint = (value: string | undefined): boolean =>
  Boolean(value && /[\p{L}\p{N}_]/u.test(value));

const _applyVocabularyRule = (
  text: string,
  rule: DictationVocabularyRule,
): string => {
  const phrase = _escapeRegExp(rule.from);
  const expression = new RegExp(`(${phrase})`, "giu");

  return text.replace(
    expression,
    (matched: string, command: string, offset: number, source: string) => {
      const commandOffset = offset + matched.indexOf(command);
      const before = _codePointBefore(source, commandOffset);
      const after = _codePointAfter(source, commandOffset + command.length);
      if (_isWordCodePoint(before) || _isWordCodePoint(after)) return matched;
      return rule.to;
    },
  );
};

export const applyWebDictationVocabulary = (
  text: string,
  rulesJson: string,
): string => {
  if (!text || !rulesJson) return text;
  return _parseVocabularyRules(rulesJson).reduce(
    (result, rule) => _applyVocabularyRule(result, rule),
    text,
  );
};

export type WebDictationHistoryItem = { text: string; at: number };

const _normalizeHistory = (value: unknown): WebDictationHistoryItem[] => {
  if (!Array.isArray(value)) return [];
  return value
    .flatMap((item): WebDictationHistoryItem[] =>
      _isRecord(item) &&
      typeof item.text === "string" &&
      item.text.trim().length > 0 &&
      typeof item.at === "number" &&
      Number.isFinite(item.at)
        ? [{ text: item.text, at: item.at }]
        : [],
    )
    .slice(0, WEB_DICTATION_HISTORY_LIMIT);
};

export const parseWebDictationHistory = (
  serialized: string | null | undefined,
): WebDictationHistoryItem[] => {
  if (!serialized) return [];
  try {
    return _normalizeHistory(JSON.parse(serialized));
  } catch {
    return [];
  }
};

export const prependWebDictationHistory = (
  history: readonly WebDictationHistoryItem[],
  item: WebDictationHistoryItem,
): WebDictationHistoryItem[] =>
  _normalizeHistory([item, ...history]).slice(0, WEB_DICTATION_HISTORY_LIMIT);

type WebDictationStorage = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

export const readWebDictationHistory = (
  storage: Pick<WebDictationStorage, "getItem">,
): WebDictationHistoryItem[] => {
  try {
    return parseWebDictationHistory(storage.getItem(WEB_DICTATION_HISTORY_KEY));
  } catch {
    return [];
  }
};

export const writeWebDictationHistory = (
  storage: Pick<WebDictationStorage, "setItem">,
  history: readonly WebDictationHistoryItem[],
): void => {
  try {
    storage.setItem(
      WEB_DICTATION_HISTORY_KEY,
      JSON.stringify(_normalizeHistory(history)),
    );
  } catch {
    // Local history is best-effort; dictation must still return its text.
  }
};

export const clearWebDictationHistory = (
  storage: Pick<WebDictationStorage, "removeItem">,
): void => {
  try {
    storage.removeItem(WEB_DICTATION_HISTORY_KEY);
  } catch {
    // Explicit sign-out still succeeds when browser storage is unavailable.
  }
};

const WEB_DICTATION_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/mp4",
] as const;

export const selectWebDictationMimeType = (
  isTypeSupported: (mimeType: string) => boolean,
): string | undefined =>
  WEB_DICTATION_MIME_TYPES.find((mimeType) => isTypeSupported(mimeType));
